import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import GoogleSignInButton from './GoogleSignInButton';
import RoleSelectionModal from './RoleSelectionModal';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import './LoginScreen.css';

const LoginScreen = ({ redirectTo }) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [googleCredential, setGoogleCredential] = useState(null);
  const [fingerprint, setFingerprint] = useState(null);

  const { loginWithGoogleWeb } = useAuth();

  // Generate browser fingerprint on component mount
  useEffect(() => {
    const getFingerprint = async () => {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      setFingerprint(result.visitorId);
    };
    getFingerprint();
  }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      const result = await loginWithGoogleWeb(
        credentialResponse.credential,
        null, // No role yet
        fingerprint,
        redirectTo
      );

      if (result.success) {
        // User signed in successfully
        // AuthContext will handle redirect based on role
      } else if (result.requiresRole) {
        // New user - show role selection modal
        setGoogleCredential(credentialResponse.credential);
        setShowRoleSelection(true);
        setLoading(false);
      } else {
        setLoading(false);
        setError(result.error || 'Authentication failed. Please try again.');
      }
    } catch (err) {
      setLoading(false);
      setError('An unexpected error occurred. Please try again.');
      console.error('Google login error:', err);
    }
  };

  const handleGoogleError = () => {
    setError('Google Sign-In failed. Please try again.');
    setLoading(false);
  };

  const handleRoleSelected = async (role) => {
    setLoading(true);
    setError('');

    try {
      const result = await loginWithGoogleWeb(
        googleCredential,
        role,
        fingerprint,
        redirectTo
      );

      if (result.success) {
        // User created successfully
        // AuthContext will handle redirect based on role
        setShowRoleSelection(false);
      } else {
        setLoading(false);
        setShowRoleSelection(false);
        setError(result.error || 'Failed to create account. Please try again.');
      }
    } catch (err) {
      setLoading(false);
      setShowRoleSelection(false);
      setError('An unexpected error occurred. Please try again.');
      console.error('Role selection error:', err);
    }
  };

  const handleRoleModalClose = () => {
    setShowRoleSelection(false);
    setLoading(false);
    setGoogleCredential(null);
  };

  return (
    <>
      <div className="login-screen">
        <div className="login-content">
          <div className="login-header">
            <div className="app-icon">🌍</div>
            <h1>Teacha</h1>
            <p className="tagline">Learn languages with native speakers</p>
          </div>

          <div className="login-welcome">
            <h2>Sign up / Sign in</h2>
            <p>Use your Google account to get started</p>
            <p className="trial-text">✨ Start your 7-day free trial today!</p>
          </div>

          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <div className="login-button-container">
            <div className="google-button-wrapper">
              {process.env.REACT_APP_GOOGLE_CLIENT_ID && process.env.REACT_APP_GOOGLE_CLIENT_ID !== 'your-google-client-id-here.apps.googleusercontent.com' ? (
                <GoogleSignInButton
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  disabled={loading || !fingerprint}
                  text={loading ? 'Signing in...' : 'Sign in with Google'}
                  theme="filled_black"
                />
              ) : (
                <div className="oauth-not-configured">
                  <p style={{ color: '#ff6b6b', padding: '20px', textAlign: 'center', background: 'rgba(255,107,107,0.1)', borderRadius: '8px' }}>
                    ⚠️ Google OAuth is not configured.<br/>
                    Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="login-footer">
            <p>By continuing, you agree to our Terms of Service and Privacy Policy</p>
          </div>
        </div>
      </div>

      {showRoleSelection && (
        <RoleSelectionModal
          onRoleSelected={handleRoleSelected}
          onClose={handleRoleModalClose}
          loading={loading}
        />
      )}
    </>
  );
};

export default LoginScreen;
